/* eslint-disable @typescript-eslint/no-explicit-any */
import { UserConsentLevel, getConsentLevelFromDocument } from '../../utils/cookieConsentUtils';
import React from 'react';

interface ManageCookieConsentStateProps {
  ignoreConsentSettings?: boolean;
  onUpdateCookieConsentLevel: (consentLevels: UserConsentLevel[]) => void;
}
/**
 * Trustarc's script sends a message containing a number[] of the updated consent levels -
 * here, we translate those levels into UserConsentLevelss
 */
const getUserConsentLevelsFromCMAPIMessage = (message: number[]) => {
  const updatedConsentLevels: UserConsentLevel[] = [UserConsentLevel.REQUIRED];

  if (message.includes(2)) {
    updatedConsentLevels.push(UserConsentLevel.FUNCTIONAL);
  }
  if (message.includes(3)) {
    updatedConsentLevels.push(UserConsentLevel.ADVERTISING);
  }
  return updatedConsentLevels;
};

export const ManageCookieConsentState: React.FunctionComponent<
  React.PropsWithChildren<ManageCookieConsentStateProps>
> = ({ ignoreConsentSettings, onUpdateCookieConsentLevel }) => {
  React.useEffect(() => {
    const windowObj = window as any;
    const origin = window?.location?.origin;
    // Trustarc code - do not modify
    windowObj.__i__ =
      windowObj.postMessage &&
      setInterval(function () {
        if (windowObj.PrivacyManagerAPI && windowObj.__i__) {
          const apiObject = {
            PrivacyManagerAPI: {
              action: 'getConsentDecision',
              timestamp: new Date().getTime(),
              self: self.location.host,
            },
          };

          windowObj.top.postMessage(JSON.stringify(apiObject), origin);
          windowObj.__i__ = clearInterval(windowObj.__i__);
        }
      }, 50);
    // End Trustarc code

    // On initial client-side load, update state with value from cookie
    onUpdateCookieConsentLevel(getConsentLevelFromDocument(false));
  }, [onUpdateCookieConsentLevel]);

  /**
   * Update state to match updated Trustarc-managed consent level (upon change in user preference)
   */
  const updateStateOnMessage = React.useCallback(
    (e: MessageEvent, d: any) => {
      // Trustarc code - do not modify
      const _dispatched_: any = {}; //Map of previously dispatched preference levels
      try {
        if (
          e.data &&
          (d = JSON.parse(e.data)) &&
          (d = d.PrivacyManagerAPI) &&
          d.capabilities &&
          d.action === 'getConsentDecision'
        ) {
          const windowObj = window as any;

          const newDecision = windowObj.PrivacyManagerAPI.callApi(
            'getGDPRConsentDecision',
            windowObj.location.host
          ).consentDecision;
          // End Trustarc code

          if (newDecision.includes(0)) {
            return;
          }

          //Update state
          onUpdateCookieConsentLevel(getUserConsentLevelsFromCMAPIMessage(newDecision));

          // Trustarc code - do not modify
          // Publish data layer events
          newDecision &&
            newDecision.forEach(function (label: any) {
              if (!_dispatched_[label]) {
                windowObj.dataLayer &&
                  windowObj.dataLayer.push({ event: 'GDPR Pref Allows ' + label });
                _dispatched_[label] = 1;
              }
            });
        }
      } catch (_error) {
        /** not a trustarc api message **/
      }
      // End Trustarc code
    },
    [onUpdateCookieConsentLevel]
  );

  React.useEffect(() => {
    // Listen for user change
    (window as any).addEventListener('message', updateStateOnMessage);
    if (!ignoreConsentSettings) {
      (window as any).setTimeout(() => {
        onUpdateCookieConsentLevel(getConsentLevelFromDocument(true));
      }, 4000);
    } else {
      onUpdateCookieConsentLevel([
        UserConsentLevel.REQUIRED,
        UserConsentLevel.FUNCTIONAL,
        UserConsentLevel.ADVERTISING,
      ]);
    }
  }, [ignoreConsentSettings, onUpdateCookieConsentLevel, updateStateOnMessage]);

  return null;
};
